import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EnhancedDesignUpload from '../components/EnhancedDesignUpload';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { useProduct } from '../hooks/useApi';
import useSEO from '../hooks/useSEO';

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: productData, loading, error } = useProduct(id);
  const { isUnderMaintenance, isComingSoon, loading: settingsLoading } = useSiteSettings();

  const [selectedFinish, setSelectedFinish] = useState('');
  const [selectedCorners, setSelectedCorners] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(null);
  const [selectedPaperType, setSelectedPaperType] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [expandedImageIndex, setExpandedImageIndex] = useState(null);
  const [designData, setDesignData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [orderCreated, setOrderCreated] = useState(null);
  const [checkoutError, setCheckoutError] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);

  React.useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Mock product data for testing when API fails
  const mockProduct = {
    name: 'Premium Business Cards',
    description: 'High-quality business cards with premium finish. Perfect for professional networking and making lasting first impressions.',
    category: 'business-cards',
    subcategory: 'Premium Cards',
    images: [
      { url: "https://i.pinimg.com/736x/3e/73/67/3e736712a10e8b84bbb33cc9a058ec5b.jpg" },
      { url: "https://i.pinimg.com/736x/1a/78/64/1a7864088f21815d8022e06f1e5f9b86.jpg" },
      { url: "https://i.pinimg.com/736x/1c/e2/57/1ce257a50d0e935b078aa24a342f5b7a.jpg" }
    ],
    specifications: {
      paperTypes: [
        { name: 'Premium Cardstock', description: '350 GSM premium quality', gsm: 350, finish: 'matte', priceMultiplier: 1 },
        { name: 'Ultra Premium', description: '400 GSM ultra premium with texture', gsm: 400, finish: 'textured', priceMultiplier: 1.3 },
        { name: 'Luxury Textured', description: '450 GSM luxury with superior quality', gsm: 450, finish: 'textured', priceMultiplier: 1.5 }
      ],
      sizes: [
        { name: 'Standard', dimensions: { width: 89, height: 51, unit: 'mm' }, priceMultiplier: 1 },
        { name: 'European', dimensions: { width: 85, height: 55, unit: 'mm' }, priceMultiplier: 1.1 }
      ],
      finishOptions: [
        { name: 'Matte', description: 'Non-reflective matte finish', priceMultiplier: 1 },
        { name: 'Gloss', description: 'High-shine glossy finish', priceMultiplier: 1.1 }
      ],
      cornerOptions: [
        { name: 'Square', description: 'Standard square corners', priceMultiplier: 1 },
        { name: 'Rounded', description: 'Elegant rounded corners', priceMultiplier: 1.15 }
      ]
    },
    pricing: [
      { quantity: 50, basePrice: 1100, discountPrice: 999, isRecommended: false },
      { quantity: 100, basePrice: 2000, discountPrice: 1799, isRecommended: false },
      { quantity: 200, basePrice: 3600, discountPrice: 2999, isRecommended: true },
      { quantity: 500, basePrice: 8000, discountPrice: 6999, isRecommended: false }
    ],
    features: [
      'Premium 350 GSM cardstock',
      'Professional matte or gloss finish',
      'Double-sided printing included',
      'Free design consultation',
      'Custom design options available'
    ]
  };

  const product = productData?.data || (error ? mockProduct : null);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const productImage = product?.images?.[0]?.url || product?.image || `${baseUrl}/logo_white_theme.png`;
  const productName = product?.name || 'Product';
  const productDescription = product?.description || `High-quality ${productName} from Kigo Prints. Professional printing services with fast delivery.`;
  const productCategory = product?.category || 'printing';

  useSEO({
    title: `${productName} | Kigo Prints`,
    description: productDescription,
    keywords: `${productName}, ${productCategory}, printing services, custom printing, Kigo Prints, professional printing`,
    image: productImage,
    type: 'product',
    structuredData: product ? {
      '@context': 'https://schema.org',
      '@type': 'Product',
      'name': productName,
      'description': productDescription,
      'image': productImage,
      'brand': {
        '@type': 'Brand',
        'name': 'Kigo Prints'
      },
      'offers': {
        '@type': 'Offer',
        'availability': 'https://schema.org/InStock',
        'priceCurrency': 'INR',
        'seller': {
          '@type': 'Organization',
          'name': 'Kigo Prints'
        }
      },
      'category': productCategory
    } : null
  });

  // Initialize default selections when product loads
  React.useEffect(() => {
    if (product?.specifications) {
      // Set default paper type
      if (!selectedPaperType && product.specifications.paperTypes?.length > 0) {
        const firstPaperType = product.specifications.paperTypes[0];
        setSelectedPaperType(typeof firstPaperType === 'object' ? firstPaperType.name : firstPaperType);
      }

      // Set default size
      if (!selectedSize && product.specifications.sizes?.length > 0) {
        setSelectedSize(product.specifications.sizes[0].name);
      }

      // Set default finish
      if (!selectedFinish && product.specifications.finishOptions?.length > 0) {
        setSelectedFinish(product.specifications.finishOptions[0].name);
      }

      // Set default corners
      if (!selectedCorners && product.specifications.cornerOptions?.length > 0) {
        setSelectedCorners(product.specifications.cornerOptions[0].name);
      }
    }
    // quantity is initialized based on cheapest computed price in a separate effect
  }, [product, selectedPaperType, selectedSize, selectedFinish, selectedCorners, selectedQuantity]);

  // Helper function to get paper type description
  const getPaperTypeDescription = (paperType) => {
    if (!paperType) return 'High-quality printing material';

    // Handle both old string format and new object format
    if (typeof paperType === 'string') {
      if (paperType.includes('350')) return 'Premium thickness and durability';
      if (paperType.includes('400')) return 'Ultra premium quality with excellent durability';
      if (paperType.includes('450')) return 'Luxury textured finish with superior quality';
      if (paperType.includes('300')) return 'Standard professional quality';
      if (paperType.includes('250')) return 'Economic yet quality option';
      return 'High-quality printing material';
    }

    // New object format
    return paperType.description || `${paperType.gsm} GSM ${paperType.finish} finish`;
  };

  // Helper function to calculate price percentage increase for a specification option
  const calculatePricePercentage = (specName, specType, pricingTier) => {
    if (!pricingTier || !pricingTier.specificationPrices) {
      return null;
    }

    const specPrices = pricingTier.specificationPrices[specType];
    if (!specPrices) {
      return null;
    }

    // Handle both Map and object formats (Maps are converted to objects in JSON)
    let specPrice;
    if (specPrices instanceof Map) {
      specPrice = specPrices.get(specName);
    } else {
      specPrice = specPrices[specName];
    }

    if (specPrice === undefined || specPrice === null) {
      return null;
    }

    const basePrice = pricingTier.discountPrice || pricingTier.basePrice;

    if (!basePrice || basePrice === 0) {
      return null;
    }

    const percentage = (specPrice / basePrice) * 100;
    return Math.round(percentage);
  };

  // Dynamic pricing calculation based on specifications
  const calculateDynamicPrice = (basePrice, pricingTier) => {
    let totalPrice = basePrice;

    // Use per-tier specification prices if available
    if (pricingTier?.specificationPrices) {
      // Paper type price
      if (selectedPaperType && pricingTier.specificationPrices.paperTypes) {
        const paperTypePrice = pricingTier.specificationPrices.paperTypes[selectedPaperType];
        if (paperTypePrice !== undefined) {
          totalPrice += paperTypePrice;
        }
      }

      // Size price
      if (selectedSize && pricingTier.specificationPrices.sizes) {
        const sizePrice = pricingTier.specificationPrices.sizes[selectedSize];
        if (sizePrice !== undefined) {
          totalPrice += sizePrice;
        }
      }

      // Finish price
      if (selectedFinish && pricingTier.specificationPrices.finishOptions) {
        const finishPrice = pricingTier.specificationPrices.finishOptions[selectedFinish];
        if (finishPrice !== undefined) {
          totalPrice += finishPrice;
        }
      }

      // Corner price
      if (selectedCorners && pricingTier.specificationPrices.cornerOptions) {
        const cornerPrice = pricingTier.specificationPrices.cornerOptions[selectedCorners];
        if (cornerPrice !== undefined) {
          totalPrice += cornerPrice;
        }
      }
    } else {
      // Fallback to multiplier system for backward compatibility
      let multiplier = 1;

      // Paper type multiplier
      if (selectedPaperType && product?.specifications?.paperTypes) {
        const paperType = product.specifications.paperTypes.find(p =>
          typeof p === 'object' ? p.name === selectedPaperType : p === selectedPaperType
        );
        if (paperType && typeof paperType === 'object' && paperType.priceMultiplier) {
          multiplier *= paperType.priceMultiplier;
        } else if (typeof paperType === 'string') {
          // Fallback for old string format
          if (paperType.includes('400')) multiplier += 0.3;
          else if (paperType.includes('450')) multiplier += 0.5;
          else if (paperType.includes('350')) multiplier += 0.15;
        }
      }

      // Size multiplier
      if (selectedSize && product?.specifications?.sizes) {
        const size = product.specifications.sizes.find(s => s.name === selectedSize);
        if (size?.priceMultiplier) {
          multiplier *= size.priceMultiplier;
        }
      }

      // Finish multiplier
      if (selectedFinish && product?.specifications?.finishOptions) {
        const finish = product.specifications.finishOptions.find(f => f.name === selectedFinish);
        if (finish?.priceMultiplier) {
          multiplier *= finish.priceMultiplier;
        }
      }

      // Corner multiplier
      if (selectedCorners && product?.specifications?.cornerOptions) {
        const corner = product.specifications.cornerOptions.find(c => c.name === selectedCorners);
        if (corner?.priceMultiplier) {
          multiplier *= corner.priceMultiplier;
        }
      }

      totalPrice = basePrice * multiplier;
    }

    return Math.round(totalPrice);
  };

  // Enhanced quantities with dynamic pricing from database
  const getQuantitiesWithDynamicPricing = () => {
    if (product?.pricing && Array.isArray(product.pricing)) {
      return product.pricing.map(pricingTier => ({
        qty: pricingTier.quantity,
        basePrice: pricingTier.basePrice,
        originalPrice: pricingTier.basePrice,
        discountPrice: pricingTier.discountPrice,
        recommended: pricingTier.isRecommended,
        pack: calculateDynamicPrice(pricingTier.discountPrice || pricingTier.basePrice, pricingTier),
        original: calculateDynamicPrice(pricingTier.basePrice, pricingTier)
      }));
    }

    // Fallback for mock data
    return [
      { qty: 50, basePrice: 1100, originalPrice: 1100 },
      { qty: 100, basePrice: 2000, originalPrice: 2000 },
      { qty: 200, basePrice: 3600, originalPrice: 3600, recommended: true },
      { qty: 500, basePrice: 8000, originalPrice: 8000 }
    ].map(item => ({
      ...item,
      pack: calculateDynamicPrice(item.basePrice * 0.9, null), // Apply 10% base discount
      original: calculateDynamicPrice(item.originalPrice, null)
    }));
  };

  const quantities = useMemo(() => getQuantitiesWithDynamicPricing(), [
    product,
    selectedPaperType,
    selectedSize,
    selectedFinish,
    selectedCorners
  ]);

  // Get the current pricing tier based on selected quantity
  const currentPricingTier = useMemo(() => {
    if (!product?.pricing || !selectedQuantity) return null;
    const tier = product.pricing.find(tier => tier.quantity === selectedQuantity);
    return tier || product.pricing[0] || null;
  }, [product?.pricing, selectedQuantity]);

  // Ensure default selection: recommended first, then cheapest
  React.useEffect(() => {
    if (product && !selectedQuantity && Array.isArray(quantities) && quantities.length > 0) {
      // First, check if there's a recommended option
      const recommended = quantities.find(q => q.recommended === true);

      if (recommended) {
        // Select recommended if it exists
        setSelectedQuantity(recommended.qty);
      } else {
        // If no recommended option, select the cheapest
        const cheapest = quantities.reduce((min, q) => {
          if (!min) return q;
          return q.pack < min.pack ? q : min;
        }, null);
        if (cheapest) {
          setSelectedQuantity(cheapest.qty);
        }
      }
    }
  }, [product, quantities, selectedQuantity]);

  const selectedQtyObj = quantities.find(q => q.qty === selectedQuantity);
  const cheapestQtyObj = Array.isArray(quantities) && quantities.length > 0
    ? quantities.reduce((min, q) => {
      if (!min) return q;
      return q.pack < min.pack ? q : min;
    }, null)
    : null;

  // Handle checkout - navigate to checkout page
  const handleCheckout = () => {
    setCheckoutError('');
    const selectedQtyObj = quantities.find(q => q.qty === selectedQuantity);
    const totalAmount = selectedQtyObj ? selectedQtyObj.pack : 0;

    if (!totalAmount || totalAmount <= 0) {
      setCheckoutError('Order value cannot be zero. Please select a valid quantity and options.');
      return;
    }

    const orderData = {
      productId: id,
      product: product,
      selectedQuantity,
      selectedFinish,
      selectedCorners,
      selectedPaperType,
      selectedSize,
      designData,
      totalAmount
    };

    navigate('/checkout', {
      state: { orderData }
    });
  };

  // Maintenance check
  if (isUnderMaintenance || isComingSoon) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="text-center max-w-2xl">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {isComingSoon ? "Coming Soon" : "Website Under Maintenance"}
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            {isComingSoon
              ? "We're working hard to bring you something amazing. Stay tuned!"
              : "We're currently performing maintenance. Product browsing is temporarily unavailable. You can still view your orders."}
          </p>
          <button
            onClick={() => navigate('/my-orders')}
            className="inline-block bg-[#008000] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#008000] transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-lg"
          >
            View My Orders
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-200 animate-pulse rounded-lg h-96"></div>
            <div className="space-y-4">
              <div className="bg-gray-200 animate-pulse rounded h-8 w-3/4"></div>
              <div className="bg-gray-200 animate-pulse rounded h-4 w-1/2"></div>
              <div className="bg-gray-200 animate-pulse rounded h-20"></div>
              <div className="bg-gray-200 animate-pulse rounded h-12 w-32"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <p className="text-gray-600">The product you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Available</h1>
          <p className="text-gray-600">This product is currently unavailable.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="container mx-auto px-4 py-4 sm:py-8 pt-20 sm:pt-32">
        {/* Product View */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* Left Column - Product Images */}
          <div className="space-y-4 lg:sticky lg:top-[6.4rem] lg:pt-2 lg:self-start">
            {/* Main Image */}
            <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100 shadow-lg">
              <img
                src={expandedImageIndex !== null && product?.images?.[expandedImageIndex]
                  ? product.images[expandedImageIndex].url
                  : product?.images?.[0]?.url || "https://i.pinimg.com/736x/3e/73/67/3e736712a10e8b84bbb33cc9a058ec5b.jpg"
                }
                alt={product?.name || "Product"}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Thumbnails */}
            <div className="grid grid-cols-4 gap-3">
              {product?.images?.slice(0, 4).map((image, index) => (
                <div
                  key={index}
                  className={`aspect-square rounded-xl overflow-hidden bg-gray-100 cursor-pointer border-2 transition-all duration-200 hover:shadow-md ${expandedImageIndex === index || (expandedImageIndex === null && index === 0)
                    ? 'border-[#008000] shadow-md'
                    : 'border-transparent'
                    }`}
                  onClick={() => setExpandedImageIndex(index)}
                >
                  <img
                    src={image.url}
                    alt={`${product?.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>

            {/* Product Description - Desktop only */}
            <div className="hidden lg:block mt-8">
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md">
                <h2 className="text-2xl font-bold mb-4">Product Description</h2>
                <div className="prose max-w-none">
                  <p className="text-gray-600 whitespace-pre-line">{product?.description}</p>
                </div>
              </div>
            </div>

            {/* Design Guidelines - Desktop only */}
            <div className="hidden lg:block mt-6">
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md">
                <h2 className="text-2xl font-bold mb-6">Design Guidelines</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                  {/* File Requirements */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">📁 File Requirements</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#008000] rounded-full mr-3"></span>
                        Format: PDF, AI, PSD, or high-res JPG/PNG
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#008000] rounded-full mr-3"></span>
                        Resolution: 300 DPI minimum
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#008000] rounded-full mr-3"></span>
                        Color Mode: CMYK preferred
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#008000] rounded-full mr-3"></span>
                        Bleed: 2mm on all sides
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#008000] rounded-full mr-3"></span>
                        File Size: Maximum 50MB
                      </li>
                    </ul>
                  </div>

                  {/* Design Tips */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">💡 Design Tips</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#008000] rounded-full mr-3"></span>
                        Keep important elements away from edges
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#008000] rounded-full mr-3"></span>
                        Use readable fonts (minimum 8pt)
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#008000] rounded-full mr-3"></span>
                        Ensure high contrast for text
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#008000] rounded-full mr-3"></span>
                        Include contact information clearly
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#008000] rounded-full mr-3"></span>
                        Consider both sides if double-sided
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Need Help Section */}
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <h4 className="font-semibold text-[#008000] mb-2">Need Design Help?</h4>
                  <p className="text-sm text-[#008000]">
                    Don't have a design ready? No problem! Our professional design team can create
                    stunning business cards for you. Contact us for custom design services.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Product Details */}
          <div className="space-y-6">

            {/* Product Title and Pricing */}
            <div className="pt-3 sm:pt-4 pb-3 sm:pb-4 px-3 sm:px-4 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">
                {product?.name || 'Premium Business Cards'}
              </h1>
              <div className="text-lg sm:text-2xl font-bold text-[#008000] mb-2 sm:mb-3">
                Rs.{(selectedQtyObj || cheapestQtyObj)?.pack || 0}
                {(() => {
                  const obj = selectedQtyObj || cheapestQtyObj;
                  if (obj?.original && obj.original !== obj.pack) {
                    return (
                      <span className="text-lg text-gray-400 line-through ml-2">Rs.{obj.original}</span>
                    );
                  }
                  return null;
                })()}
              </div>
              <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                {product?.description || 'High-quality business cards with premium finish. Perfect for professional networking and making lasting first impressions.'}
              </p>
            </div>

            {/* Key Features */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4">
              <h3 className="font-semibold text-[#008000] mb-2 sm:mb-3 text-xs sm:text-sm">What's Included ?</h3>
              <div className="grid grid-cols-2 gap-2">
                {product?.features ? product.features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <svg className="w-3 h-3 text-[#008000] mt-0.5 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span className="text-xs sm:text-sm text-[#008000] leading-tight">{feature}</span>
                  </div>
                )) : (
                  <>
                    <div className="flex items-start">
                      <svg className="w-3 h-3 text-[#008000] mt-0.5 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span className="text-xs text-[#008000] leading-tight">Premium 350 GSM cardstock</span>
                    </div>
                    <div className="flex items-start">
                      <svg className="w-3 h-3 text-[#008000] mt-0.5 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span className="text-xs text-[#008000] leading-tight">Double-sided printing</span>
                    </div>
                    <div className="flex items-start">
                      <svg className="w-3 h-3 text-[#008000] mt-0.5 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span className="text-xs text-[#008000] leading-tight">CMYK printing technology</span>
                    </div>
                    <div className="flex items-start">
                      <svg className="w-3 h-3 text-[#008000] mt-0.5 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span className="text-xs text-[#008000] leading-tight">Custom design options</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Quantity Selection Dropdown */}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">Select Quantity</h3>
              <div
                className={`w-full p-3 sm:p-4 border rounded-xl bg-white flex justify-between items-center cursor-pointer transition-all ${openDropdown === 'quantity' ? 'border-[#008000] ring-1 ring-[#008000] shadow-md' : 'border-gray-200 hover:border-gray-300'}`}
                onClick={() => setOpenDropdown(openDropdown === 'quantity' ? null : 'quantity')}
              >
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm sm:text-base">{selectedQuantity ? `${selectedQuantity} pieces` : 'Select Quantity'}</span>
                    {selectedQuantity && quantities.find(q => q.qty === selectedQuantity)?.recommended && (
                      <span className="px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs bg-green-100 text-[#008000] rounded-full">
                        Recommended
                      </span>
                    )}
                  </div>
                  {selectedQuantity && (
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-sm font-semibold text-[#008000]">Rs.{quantities.find(q => q.qty === selectedQuantity)?.pack}</span>
                      {quantities.find(q => q.qty === selectedQuantity)?.original && quantities.find(q => q.qty === selectedQuantity)?.original !== quantities.find(q => q.qty === selectedQuantity)?.pack && (
                        <span className="text-xs text-gray-400 line-through">
                          Rs.{quantities.find(q => q.qty === selectedQuantity)?.original}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <svg className={`w-5 h-5 text-gray-500 transition-transform ${openDropdown === 'quantity' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {openDropdown === 'quantity' && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {quantities.map((qty) => (
                    <div
                      key={qty.qty}
                      className={`p-3 sm:p-4 cursor-pointer hover:bg-green-50 transition-colors border-b last:border-b-0 border-gray-100 ${selectedQuantity === qty.qty ? 'bg-green-50' : ''
                        }`}
                      onClick={() => {
                        setSelectedQuantity(qty.qty);
                        setOpenDropdown(null);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-sm sm:text-base text-gray-900">{qty.qty} pieces</h4>
                            {qty.recommended && (
                              <span className="px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs bg-green-100 text-[#008000] rounded-full">
                                Recommended
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 sm:mt-1">
                            <span className="font-semibold text-[#008000] text-sm">Rs.{qty.pack}</span>
                            {qty.original && qty.original !== qty.pack && (
                              <span className="text-xs text-gray-400 line-through">
                                Rs.{qty.original}
                              </span>
                            )}
                          </div>
                        </div>
                        {selectedQuantity === qty.qty && (
                          <svg className="w-5 h-5 text-[#008000]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Product Options */}
            <div className="space-y-6">

              {/* Choose Finish */}
              {product?.specifications?.finishOptions?.length > 0 && (
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">Choose Finish</h3>
                  <div
                    className={`w-full p-3 sm:p-4 border rounded-xl bg-white flex justify-between items-center cursor-pointer transition-all ${openDropdown === 'finish' ? 'border-[#008000] ring-1 ring-[#008000] shadow-md' : 'border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setOpenDropdown(openDropdown === 'finish' ? null : 'finish')}
                  >
                    <div>
                      <span className="font-semibold text-sm sm:text-base">{selectedFinish || 'Select Finish'}</span>
                      {selectedFinish && product.specifications.finishOptions.find(f => f.name === selectedFinish)?.description && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {product.specifications.finishOptions.find(f => f.name === selectedFinish)?.description}
                        </p>
                      )}
                    </div>
                    <svg className={`w-5 h-5 text-gray-500 transition-transform ${openDropdown === 'finish' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {openDropdown === 'finish' && (
                    <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {product.specifications.finishOptions.map((finish) => {
                        const pricePercentage = currentPricingTier
                          ? calculatePricePercentage(finish.name, 'finishOptions', currentPricingTier)
                          : (finish.priceMultiplier && finish.priceMultiplier !== 1 ? Math.round((finish.priceMultiplier - 1) * 100) : null);

                        return (
                          <div
                            key={finish.name}
                            className={`p-3 sm:p-4 cursor-pointer hover:bg-green-50 transition-colors border-b last:border-b-0 border-gray-100 ${selectedFinish === finish.name ? 'bg-green-50' : ''
                              }`}
                            onClick={() => {
                              setSelectedFinish(finish.name);
                              setOpenDropdown(null);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-sm sm:text-base text-gray-900">{finish.name}</h4>
                                <p className="text-xs sm:text-sm text-gray-600">{finish.description}</p>
                                {pricePercentage !== null && pricePercentage !== 0 ? (
                                  <p className="text-[10px] sm:text-xs text-orange-600 font-medium mt-0.5 sm:mt-1">
                                    {pricePercentage > 0 ? '+' : ''}{pricePercentage}% price
                                  </p>
                                ) : (
                                  <p className="text-[10px] sm:text-xs text-gray-500 font-medium mt-0.5 sm:mt-1">Standard price</p>
                                )}
                              </div>
                              {selectedFinish === finish.name && (
                                <svg className="w-5 h-5 text-[#008000]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Choose Corners */}
              {product?.specifications?.cornerOptions?.length > 0 && (
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">Choose Corners</h3>
                  <div
                    className={`w-full p-3 sm:p-4 border rounded-xl bg-white flex justify-between items-center cursor-pointer transition-all ${openDropdown === 'corners' ? 'border-[#008000] ring-1 ring-[#008000] shadow-md' : 'border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setOpenDropdown(openDropdown === 'corners' ? null : 'corners')}
                  >
                    <div>
                      <span className="font-semibold text-sm sm:text-base">{selectedCorners || 'Select Corners'}</span>
                      {selectedCorners && product.specifications.cornerOptions.find(c => c.name === selectedCorners)?.description && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {product.specifications.cornerOptions.find(c => c.name === selectedCorners)?.description}
                        </p>
                      )}
                    </div>
                    <svg className={`w-5 h-5 text-gray-500 transition-transform ${openDropdown === 'corners' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {openDropdown === 'corners' && (
                    <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {product.specifications.cornerOptions.map((corner) => {
                        const pricePercentage = currentPricingTier
                          ? calculatePricePercentage(corner.name, 'cornerOptions', currentPricingTier)
                          : (corner.priceMultiplier && corner.priceMultiplier !== 1 ? Math.round((corner.priceMultiplier - 1) * 100) : null);

                        return (
                          <div
                            key={corner.name}
                            className={`p-3 sm:p-4 cursor-pointer hover:bg-green-50 transition-colors border-b last:border-b-0 border-gray-100 ${selectedCorners === corner.name ? 'bg-green-50' : ''
                              }`}
                            onClick={() => {
                              setSelectedCorners(corner.name);
                              setOpenDropdown(null);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-sm sm:text-base text-gray-900">{corner.name}</h4>
                                <p className="text-xs sm:text-sm text-gray-600">{corner.description}</p>
                                {pricePercentage !== null && pricePercentage !== 0 ? (
                                  <p className="text-[10px] sm:text-xs text-orange-600 font-medium mt-0.5 sm:mt-1">
                                    {pricePercentage > 0 ? '+' : ''}{pricePercentage}% price
                                  </p>
                                ) : (
                                  <p className="text-[10px] sm:text-xs text-gray-500 font-medium mt-0.5 sm:mt-1">Standard price</p>
                                )}
                              </div>
                              {selectedCorners === corner.name && (
                                <svg className="w-5 h-5 text-[#008000]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Paper Type Selection */}
              {product?.specifications?.paperTypes?.length > 0 && (
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">Choose Paper Type</h3>
                  <div
                    className={`w-full p-3 sm:p-4 border rounded-xl bg-white flex justify-between items-center cursor-pointer transition-all ${openDropdown === 'paperType' ? 'border-[#008000] ring-1 ring-[#008000] shadow-md' : 'border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setOpenDropdown(openDropdown === 'paperType' ? null : 'paperType')}
                  >
                    <div>
                      <span className="font-semibold text-sm sm:text-base">{selectedPaperType || 'Select Paper Type'}</span>
                      {selectedPaperType && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {(() => {
                            const pType = product.specifications.paperTypes.find(p => typeof p === 'object' ? p.name === selectedPaperType : p === selectedPaperType);
                            return pType ? (typeof pType === 'object' ? pType.description : getPaperTypeDescription(pType)) : '';
                          })()}
                        </p>
                      )}
                    </div>
                    <svg className={`w-5 h-5 text-gray-500 transition-transform ${openDropdown === 'paperType' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {openDropdown === 'paperType' && (
                    <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {product.specifications.paperTypes.map((paperType) => {
                        const isObject = typeof paperType === 'object';
                        const name = isObject ? paperType.name : paperType;
                        const description = isObject ? paperType.description : getPaperTypeDescription(paperType);

                        // Calculate price percentage from per-tier pricing or fallback to multiplier
                        const pricePercentage = currentPricingTier
                          ? calculatePricePercentage(name, 'paperTypes', currentPricingTier)
                          : (isObject && paperType.priceMultiplier && paperType.priceMultiplier !== 1
                            ? Math.round((paperType.priceMultiplier - 1) * 100)
                            : null);

                        return (
                          <div
                            key={name}
                            className={`p-3 sm:p-4 cursor-pointer hover:bg-green-50 transition-colors border-b last:border-b-0 border-gray-100 ${selectedPaperType === name ? 'bg-green-50' : ''
                              }`}
                            onClick={() => {
                              setSelectedPaperType(name);
                              setOpenDropdown(null);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-sm sm:text-base text-gray-900">{name}</h4>
                                <p className="text-xs sm:text-sm text-gray-600">{description}</p>
                                {isObject && paperType.gsm && (
                                  <p className="text-xs sm:text-sm text-[#008000] font-medium mt-0.5 sm:mt-1">{paperType.gsm} GSM</p>
                                )}
                                {pricePercentage !== null && pricePercentage !== 0 && (
                                  <p className="text-xs sm:text-sm text-orange-600 font-medium mt-0.5 sm:mt-1">
                                    {pricePercentage > 0 ? '+' : ''}{pricePercentage}% price
                                  </p>
                                )}
                              </div>
                              {selectedPaperType === name && (
                                <svg className="w-5 h-5 text-[#008000]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Size Selection */}
              {product?.specifications?.sizes?.length > 0 && (
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">Choose Size</h3>
                  <div
                    className={`w-full p-3 sm:p-4 border rounded-xl bg-white flex justify-between items-center cursor-pointer transition-all ${openDropdown === 'size' ? 'border-[#008000] ring-1 ring-[#008000] shadow-md' : 'border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setOpenDropdown(openDropdown === 'size' ? null : 'size')}
                  >
                    <div>
                      <span className="font-semibold text-sm sm:text-base">{selectedSize || 'Select Size'}</span>
                      {selectedSize && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {(() => {
                            const sizeObj = product.specifications.sizes.find(s => s.name === selectedSize);
                            return sizeObj?.dimensions ? `${sizeObj.dimensions.width} × ${sizeObj.dimensions.height} ${sizeObj.dimensions.unit}` : '';
                          })()}
                        </p>
                      )}
                    </div>
                    <svg className={`w-5 h-5 text-gray-500 transition-transform ${openDropdown === 'size' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {openDropdown === 'size' && (
                    <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {product.specifications.sizes.map((size) => {
                        const pricePercentage = currentPricingTier
                          ? calculatePricePercentage(size.name, 'sizes', currentPricingTier)
                          : (size.priceMultiplier && size.priceMultiplier !== 1
                            ? Math.round((size.priceMultiplier - 1) * 100)
                            : null);

                        return (
                          <div
                            key={size.name}
                            className={`p-3 sm:p-4 cursor-pointer hover:bg-green-50 transition-colors border-b last:border-b-0 border-gray-100 ${selectedSize === size.name ? 'bg-green-50' : ''
                              }`}
                            onClick={() => {
                              setSelectedSize(size.name);
                              setOpenDropdown(null);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-sm sm:text-base text-gray-900">{size.name}</h4>
                                {size.dimensions && (
                                  <p className="text-xs sm:text-sm text-gray-600">
                                    {size.dimensions.width} × {size.dimensions.height} {size.dimensions.unit}
                                  </p>
                                )}
                                {pricePercentage !== null && pricePercentage !== 0 && (
                                  <p className="text-xs sm:text-sm text-orange-600 font-medium mt-0.5 sm:mt-1">
                                    {pricePercentage > 0 ? '+' : ''}{pricePercentage}% price
                                  </p>
                                )}
                              </div>
                              {selectedSize === size.name && (
                                <svg className="w-5 h-5 text-[#008000]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Design Upload Section */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">Design Upload</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                  Upload your own design or proceed without a design (we'll contact you for requirements).
                </p>
                <EnhancedDesignUpload
                  product={product}
                  onDesignSelect={setDesignData}
                  designData={designData}
                />
              </div>

              {/* Browse Templates Button */}
              <div className="border border-gray-300 rounded-xl p-4 sm:p-5">
                <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">Need Design Inspiration?</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                  Browse our collection of professional templates to get started quickly.
                </p>
                <button
                  onClick={() => navigate('/templates')}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-2 sm:py-3 px-4 sm:px-6 rounded-xl transition-all duration-300 ease-in-out hover:scale-[1.02] flex items-center justify-center gap-1.5 sm:gap-2 shadow-md hover:shadow-lg text-sm sm:text-base"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                  </svg>
                  Browse Templates
                </button>
              </div>


            </div>

            {/* Order Summary */}
            <div className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200 shadow-md">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Order Summary</h3>

              {/* Checkout Error Message */}
              {checkoutError && (
                <div className="mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-red-800">{checkoutError}</p>
                  </div>
                  <button
                    onClick={() => setCheckoutError('')}
                    className="ml-2 text-red-600 hover:text-red-800 transition-all duration-300 ease-in-out hover:scale-110"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              )}

              <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                <div className="flex justify-between">
                  <span>Product:</span>
                  <span className="font-medium">{product?.name || 'Premium Business Cards'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Quantity:</span>
                  <span className="font-medium">{selectedQuantity}</span>
                </div>
                <div className="flex justify-between">
                  <span>Finish:</span>
                  <span className="font-medium">{selectedFinish}</span>
                </div>
                <div className="flex justify-between">
                  <span>Corners:</span>
                  <span className="font-medium">{selectedCorners}</span>
                </div>
                {selectedSize && (
                  <div className="flex justify-between">
                    <span>Size:</span>
                    <span className="font-medium">{selectedSize}</span>
                  </div>
                )}
                {selectedPaperType && (
                  <div className="flex justify-between">
                    <span>Paper Type:</span>
                    <span className="font-medium">{selectedPaperType}</span>
                  </div>
                )}
                {designData ? (
                  <div className="flex justify-between">
                    <span>Design:</span>
                    <span className="font-medium text-[#008000]">
                      {Array.isArray(designData)
                        ? `${designData.length} Custom Upload${designData.length > 1 ? 's' : ''}`
                        : 'Custom Upload'}
                    </span>
                  </div>
                ) : (
                  <div className="flex justify-between">
                    <span>Design:</span>
                    <span className="font-medium text-orange-600">To be provided</span>
                  </div>
                )}
                <div className="border-t pt-2 mt-3 sm:mt-4">
                  <div className="flex justify-between text-base sm:text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-[#008000]">Rs.{(selectedQtyObj || cheapestQtyObj)?.pack || 0}</span>
                  </div>
                  <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">Includes all customizations and taxes</p>
                </div>
              </div>

              {/* Buy Now Button */}
              <button
                onClick={handleCheckout}
                className="w-full mt-4 sm:mt-6 py-2.5 sm:py-3 rounded-xl font-medium flex items-center justify-center transition-all duration-300 ease-in-out hover:scale-[1.02] shadow-md hover:shadow-lg bg-[#008000] hover:bg-[#008000] text-white text-sm sm:text-base"
              >
                Proceed to Checkout - Rs.{(selectedQtyObj || cheapestQtyObj)?.pack || 0}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Product Description */}
        <div className="lg:hidden mt-8 sm:mt-16">
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-md">
            <h2 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-4">Product Description</h2>
            <div className="prose max-w-none">
              <p className="text-xs sm:text-sm text-gray-600 whitespace-pre-line">{product?.description}</p>
            </div>
          </div>
        </div>

        {/* Additional Sections */}
        <div className="mt-8 sm:mt-16">

          {/* Delivery Information & Need Help Side-by-Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Delivery Information */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-md">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">🚚 Delivery Information</h3>
              <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                <div className="flex items-center">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></div>
                  <span>Free delivery on orders above Rs.500</span>
                </div>
                <div className="flex items-center">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></div>
                  <span>Standard delivery: 3-5 business days</span>
                </div>
                <div className="flex items-center">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></div>
                  <span>Kigo delivery: 1-2 business days</span>
                </div>
                <div className="flex items-center">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></div>
                  <span>Track your order in real-time</span>
                </div>
              </div>
            </div>

            {/* Help & Support */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-md">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">💬 Need Help?</h3>
              <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                <p className="text-gray-600">
                  Our design experts are here to help you create the perfect business cards.
                </p>
                <div className="space-y-1.5 sm:space-y-2">
                  <div className="flex items-center">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#008000] mr-1.5 sm:mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    <span>+91 80801 84435</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#008000] mr-1.5 sm:mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    <span>support@xpressprint.in</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Mobile Design Guidelines */}
        <div className="lg:hidden mt-8 sm:mt-16">
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-md">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Design Guidelines</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">

              {/* File Requirements */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">📁 File Requirements</h3>
                <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600">
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></span>
                    Format: PDF, AI, PSD, or high-res JPG/PNG
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></span>
                    Resolution: 300 DPI minimum
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></span>
                    Color Mode: CMYK preferred
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></span>
                    Bleed: 2mm on all sides
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></span>
                    File Size: Maximum 50MB
                  </li>
                </ul>
              </div>

              {/* Design Tips */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">💡 Design Tips</h3>
                <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600">
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></span>
                    Keep important elements away from edges
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></span>
                    Use readable fonts (minimum 8pt)
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></span>
                    Ensure high contrast for text
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></span>
                    Include contact information clearly
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#008000] rounded-full mr-2 sm:mr-3 flex-shrink-0"></span>
                    Consider both sides if double-sided
                  </li>
                </ul>
              </div>
            </div>

            {/* Need Help Section */}
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-xl">
              <h4 className="font-semibold text-sm sm:text-base text-[#008000] mb-1.5 sm:mb-2">Need Design Help?</h4>
              <p className="text-xs sm:text-sm text-[#008000]">
                Don't have a design ready? No problem! Our professional design team can create
                stunning business cards for you. Contact us for custom design services.
              </p>
            </div>
          </div>
        </div>

        {/* Mobile Fixed Bottom Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 sm:p-4 z-50 lg:hidden shadow-2xl">
          {checkoutError && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div className="flex-1">
                <p className="text-xs text-red-800">{checkoutError}</p>
              </div>
              <button
                onClick={() => setCheckoutError('')}
                className="ml-2 text-red-600 hover:text-red-800 transition-all duration-300 ease-in-out hover:scale-110"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          )}
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <div>
              <div className="text-lg sm:text-xl font-bold text-[#008000]">
                Rs.{(selectedQtyObj || cheapestQtyObj)?.pack || 0}
              </div>
              <div className="text-xs sm:text-sm text-gray-500">
                {(selectedQuantity || (cheapestQtyObj && cheapestQtyObj.qty))} pieces • {selectedFinish} • {selectedCorners}
              </div>
            </div>
          </div>
          <button
            onClick={handleCheckout}
            className="w-full py-2.5 sm:py-3 rounded-xl font-medium flex items-center justify-center transition-all duration-300 ease-in-out hover:scale-[1.02] shadow-md hover:shadow-lg bg-[#008000] hover:bg-[#008000] text-white text-sm sm:text-base"
          >
            Proceed to Checkout - Rs.{(selectedQtyObj || cheapestQtyObj)?.pack || 0}
          </button>
        </div>

        {/* Add bottom padding to prevent overlap with fixed mobile bar */}
        <div className="h-20 lg:hidden"></div>
      </div>
    </div>
  );
};

export default ProductPage;
